import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { AuthContext } from '../../App';

function RegisterForm({ onToggleMode, referralInfo, initialEmail }) {
  const [formData, setFormData] = useState({
    email: initialEmail || '',
    username: '',
    phone: '',
    country_code: '+1',
    password: '',
    first_name: '',
    last_name: '',
    referral_code: referralInfo?.code || '',
  });

  // Update referral_code when referralInfo prop changes
  useEffect(() => {
    if (referralInfo?.code) {
      setFormData(prev => ({ ...prev, referral_code: referralInfo.code }));
    }
  }, [referralInfo]);

  // #83 — prefill the email carried over from the beta gate (PreLaunchModal → real signup)
  useEffect(() => {
    if (initialEmail) {
      setFormData(prev => ({ ...prev, email: initialEmail }));
    }
  }, [initialEmail]);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCodes, setVerificationCodes] = useState({
    email_code: '',
    sms_code: ''
  });
  const [pendingUserId, setPendingUserId] = useState(null);
  const [verifyLaterMessage, setVerifyLaterMessage] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(false);
  const [showCancelChoice, setShowCancelChoice] = useState(false);
  const [accountCancelledMessage, setAccountCancelledMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterPw, setShowRegisterPw] = useState(false);
  const { register } = React.useContext(AuthContext);

  // ── Beta consent gate (migration 113) ────────────────────────────────────────────────────
  // TWO separate acknowledgements, deliberately. The standard ToS + Privacy Policy share one
  // checkbox (ordinary clickwrap). The beta terms get their own, plus a typed signature, because
  // they contain an ADVERSE clause — an unused account is suspended at 21 days and its health data
  // permanently deleted at 30. Burying an unusual adverse term inside a blanket "I agree to
  // everything" is what gets clickwrap set aside; isolating it makes it more defensible.
  //
  // This is a courtesy gate. The SERVER enforces it (auth_router register_user) — never rely on
  // the browser to decide whether a control applies.
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [acceptedBeta, setAcceptedBeta] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [consentDoc, setConsentDoc] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  // Terms text + versions come from the SERVER so what a person is shown can never drift from
  // what is recorded against their name.
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/consent-documents`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setConsentDoc(d); })
      .catch(() => { /* degrade: the server still enforces; see consentReady below */ });
  }, []);

  const trialDays = consentDoc?.trial_days ?? 60;
  const inact = consentDoc?.inactivity || {};
  const consentReady = acceptedTos && acceptedBeta && signedName.trim().length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          accepted_tos: acceptedTos,
          accepted_beta_terms: acceptedBeta,
          beta_terms_signed_name: signedName.trim(),
        })
      });

      if (response.ok) {
        const userData = await response.json();
        setPendingUserId(userData.id);
        setShowVerificationModal(true);
        sessionStorage.removeItem('healist_referral');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/verify-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          phone: formData.phone,
          email_code: verificationCodes.email_code,
          sms_code: verificationCodes.sms_code
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Verification successful! You can now sign in.');
        setShowVerificationModal(false);
        onToggleMode();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLater = () => {
    setVerifyLaterMessage(true);
  };

  const handleVerifyLaterOK = () => {
    setVerifyLaterMessage(false);
    setShowVerificationModal(false);
    onToggleMode();
  };

  const handleVerifyLaterCancel = () => {
    setVerifyLaterMessage(false);
  };

  const handleCancel = () => {
    setShowCancelChoice(true);
  };

  const handleCancelAccountCreation = async () => {
    setShowCancelChoice(false);
    if (pendingUserId) {
      try {
        await fetch(`${API_BASE}/api/auth/users/${pendingUserId}`, {
          method: 'DELETE'
        });
      } catch (err) {
        console.error('Failed to delete account:', err);
      }
    }
    setAccountCancelledMessage(true);
  };

  const handleAccountCancelledOK = () => {
    setAccountCancelledMessage(false);
    setShowVerificationModal(false);
    setFormData({
      email: '',
      username: '',
      password: '',
      phone: '',
      country_code: '+1',
      first_name: '',
      last_name: ''
    });
    setVerificationCodes({ email_code: '', sms_code: '' });
    setPendingUserId(null);
    onToggleMode();
  };

  const handleCancelVerificationOnly = () => {
    setShowCancelChoice(false);
    setCancelMessage(true);
  };

  const handleCancelOK = () => {
    setCancelMessage(false);
    setShowVerificationModal(false);
    onToggleMode();
  };

  const handleResendCodes = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/auth/resend-codes?user_id=${pendingUserId}`, {
        method: 'POST'
      });
      alert('Verification codes resent!');
    } catch (err) {
      setError('Failed to resend codes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="auth-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        {referralInfo && (
          <div className="rlp-referralBanner">
            <div className="rlp-referralIcon">&#127881;</div>
            <h3 className="rlp-referralTitle">
              You've been invited by {referralInfo.referrerName}!
            </h3>
            <p className="rlp-referralSubtext">
              Create your account to start your <strong>{referralInfo.bonusTrialDays}-day free trial</strong> —
              that's {referralInfo.bonusTrialDays - 7} extra days, courtesy of your friend!
            </p>
          </div>
        )}
        <h2>Sign Up</h2>

        {error && (
          <div className="alert-error-box">
            {error}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            className="form-input"
            placeholder="... will be verified"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Telephone #</label>
          <div className="flex gap-2">
            <select
              name="country_code"
              value={formData.country_code}
              onChange={handleChange}
              className="select-country-code"
            >
              <option value="+1">🇺🇸 United States (+1)</option>
              <option value="+1">🇨🇦 Canada (+1)</option>
              <option value="+44">🇬🇧 United Kingdom (+44)</option>
              <option value="+61">🇦🇺 Australia (+61)</option>
              <option value="+64">🇳🇿 New Zealand (+64)</option>
              <option value="+353">🇮🇪 Ireland (+353)</option>
              <option value="+27">🇿🇦 South Africa (+27)</option>
              <option value="+234">🇳🇬 Nigeria (+234)</option>
              <option value="+233">🇬🇭 Ghana (+233)</option>
              <option value="+254">🇰🇪 Kenya (+254)</option>
              <option value="+256">🇺🇬 Uganda (+256)</option>
              <option value="+65">🇸🇬 Singapore (+65)</option>
              <option value="+63">🇵🇭 Philippines (+63)</option>
              <option value="+91">🇮🇳 India (+91)</option>
              <option value="+92">🇵🇰 Pakistan (+92)</option>
              <option value="+1">🇯🇲 Jamaica (+1)</option>
              <option value="+1">🇹🇹 Trinidad & Tobago (+1)</option>
              <option value="+1">🇧🇸 Bahamas (+1)</option>
              <option value="+1">🇧🇧 Barbados (+1)</option>
              <option value="+7">🇷🇺 Russia (+7)</option>
              <option value="+7">🇰🇿 Kazakhstan (+7)</option>
              <option value="+20">🇪🇬 Egypt (+20)</option>
              <option value="+30">🇬🇷 Greece (+30)</option>
              <option value="+31">🇳🇱 Netherlands (+31)</option>
              <option value="+32">🇧🇪 Belgium (+32)</option>
              <option value="+33">🇫🇷 France (+33)</option>
              <option value="+34">🇪🇸 Spain (+34)</option>
              <option value="+36">🇭🇺 Hungary (+36)</option>
              <option value="+39">🇮🇹 Italy (+39)</option>
              <option value="+40">🇷🇴 Romania (+40)</option>
              <option value="+41">🇨🇭 Switzerland (+41)</option>
              <option value="+43">🇦🇹 Austria (+43)</option>
              <option value="+45">🇩🇰 Denmark (+45)</option>
              <option value="+46">🇸🇪 Sweden (+46)</option>
              <option value="+47">🇳🇴 Norway (+47)</option>
              <option value="+48">🇵🇱 Poland (+48)</option>
              <option value="+49">🇩🇪 Germany (+49)</option>
              <option value="+51">🇵🇪 Peru (+51)</option>
              <option value="+52">🇲🇽 Mexico (+52)</option>
              <option value="+53">🇨🇺 Cuba (+53)</option>
              <option value="+54">🇦🇷 Argentina (+54)</option>
              <option value="+55">🇧🇷 Brazil (+55)</option>
              <option value="+56">🇨🇱 Chile (+56)</option>
              <option value="+57">🇨🇴 Colombia (+57)</option>
              <option value="+58">🇻🇪 Venezuela (+58)</option>
              <option value="+60">🇲🇾 Malaysia (+60)</option>
              <option value="+62">🇮🇩 Indonesia (+62)</option>
              <option value="+66">🇹🇭 Thailand (+66)</option>
              <option value="+81">🇯🇵 Japan (+81)</option>
              <option value="+82">🇰🇷 South Korea (+82)</option>
              <option value="+84">🇻🇳 Vietnam (+84)</option>
              <option value="+86">🇨🇳 China (+86)</option>
              <option value="+90">🇹🇷 Turkey (+90)</option>
              <option value="+93">🇦🇫 Afghanistan (+93)</option>
              <option value="+94">🇱🇰 Sri Lanka (+94)</option>
              <option value="+95">🇲🇲 Myanmar (+95)</option>
              <option value="+98">🇮🇷 Iran (+98)</option>
              <option value="+211">🇸🇸 South Sudan (+211)</option>
              <option value="+212">🇲🇦 Morocco (+212)</option>
              <option value="+213">🇩🇿 Algeria (+213)</option>
              <option value="+216">🇹🇳 Tunisia (+216)</option>
              <option value="+218">🇱🇾 Libya (+218)</option>
              <option value="+220">🇬🇲 Gambia (+220)</option>
              <option value="+221">🇸🇳 Senegal (+221)</option>
              <option value="+222">🇲🇷 Mauritania (+222)</option>
              <option value="+223">🇲🇱 Mali (+223)</option>
              <option value="+224">🇬🇳 Guinea (+224)</option>
              <option value="+225">🇨🇮 Côte d'Ivoire (+225)</option>
              <option value="+226">🇧🇫 Burkina Faso (+226)</option>
              <option value="+227">🇳🇪 Niger (+227)</option>
              <option value="+228">🇹🇬 Togo (+228)</option>
              <option value="+229">🇧🇯 Benin (+229)</option>
              <option value="+230">🇲🇺 Mauritius (+230)</option>
              <option value="+231">🇱🇷 Liberia (+231)</option>
              <option value="+232">🇸🇱 Sierra Leone (+232)</option>
              <option value="+235">🇹🇩 Chad (+235)</option>
              <option value="+236">🇨🇫 Central African Republic (+236)</option>
              <option value="+237">🇨🇲 Cameroon (+237)</option>
              <option value="+238">🇨🇻 Cape Verde (+238)</option>
              <option value="+239">🇸🇹 São Tomé & Príncipe (+239)</option>
              <option value="+240">🇬🇶 Equatorial Guinea (+240)</option>
              <option value="+241">🇬🇦 Gabon (+241)</option>
              <option value="+242">🇨🇬 Republic of the Congo (+242)</option>
              <option value="+243">🇨🇩 DR Congo (+243)</option>
              <option value="+244">🇦🇴 Angola (+244)</option>
              <option value="+245">🇬🇼 Guinea-Bissau (+245)</option>
              <option value="+248">🇸🇨 Seychelles (+248)</option>
              <option value="+249">🇸🇩 Sudan (+249)</option>
              <option value="+250">🇷🇼 Rwanda (+250)</option>
              <option value="+251">🇪🇹 Ethiopia (+251)</option>
              <option value="+252">🇸🇴 Somalia (+252)</option>
              <option value="+253">🇩🇯 Djibouti (+253)</option>
              <option value="+255">🇹🇿 Tanzania (+255)</option>
              <option value="+257">🇧🇮 Burundi (+257)</option>
              <option value="+258">🇲🇿 Mozambique (+258)</option>
              <option value="+260">🇿🇲 Zambia (+260)</option>
              <option value="+261">🇲🇬 Madagascar (+261)</option>
              <option value="+262">🇷🇪 Réunion / Mayotte (+262)</option>
              <option value="+263">🇿🇼 Zimbabwe (+263)</option>
              <option value="+264">🇳🇦 Namibia (+264)</option>
              <option value="+265">🇲🇼 Malawi (+265)</option>
              <option value="+266">🇱🇸 Lesotho (+266)</option>
              <option value="+267">🇧🇼 Botswana (+267)</option>
              <option value="+268">🇸🇿 Eswatini (+268)</option>
              <option value="+269">🇰🇲 Comoros (+269)</option>
              <option value="+350">🇬🇮 Gibraltar (+350)</option>
              <option value="+351">🇵🇹 Portugal (+351)</option>
              <option value="+352">🇱🇺 Luxembourg (+352)</option>
              <option value="+354">🇮🇸 Iceland (+354)</option>
              <option value="+355">🇦🇱 Albania (+355)</option>
              <option value="+356">🇲🇹 Malta (+356)</option>
              <option value="+357">🇨🇾 Cyprus (+357)</option>
              <option value="+358">🇫🇮 Finland (+358)</option>
              <option value="+359">🇧🇬 Bulgaria (+359)</option>
              <option value="+370">🇱🇹 Lithuania (+370)</option>
              <option value="+371">🇱🇻 Latvia (+371)</option>
              <option value="+372">🇪🇪 Estonia (+372)</option>
              <option value="+373">🇲🇩 Moldova (+373)</option>
              <option value="+374">🇦🇲 Armenia (+374)</option>
              <option value="+375">🇧🇾 Belarus (+375)</option>
              <option value="+376">🇦🇩 Andorra (+376)</option>
              <option value="+377">🇲🇨 Monaco (+377)</option>
              <option value="+378">🇸🇲 San Marino (+378)</option>
              <option value="+380">🇺🇦 Ukraine (+380)</option>
              <option value="+381">🇷🇸 Serbia (+381)</option>
              <option value="+382">🇲🇪 Montenegro (+382)</option>
              <option value="+383">🇽🇰 Kosovo (+383)</option>
              <option value="+385">🇭🇷 Croatia (+385)</option>
              <option value="+386">🇸🇮 Slovenia (+386)</option>
              <option value="+387">🇧🇦 Bosnia & Herzegovina (+387)</option>
              <option value="+389">🇲🇰 North Macedonia (+389)</option>
            </select>
            <input
              type="tel"
              name="phone"
              className="form-input flex-1"
              placeholder="... will be verified"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          <small className="text-helper-block">
            You agree that we may send you action verification/notification messages via SMS - we will never sell your phone number or send you marketing messages via SMS
          </small>
        </div>
        <div className="consent-box">
          <label className="consent-label">
            <input
              type="checkbox"
              required
              className="consent-checkbox"
            />
            <span className="consent-text">
              I agree to receive SMS verification codes from Healist for account security and authentication purposes.
            </span>
          </label>
        </div>
        <div className="form-group">
          <label className="form-label">Username</label>
          <input
            type="text"
            name="username"
            className="form-input"
            value={formData.username}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">First Name</label>
          <input
            type="text"
            name="first_name"
            className="form-input"
            value={formData.first_name}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Last Name</label>
          <input
            type="text"
            name="last_name"
            className="form-input"
            value={formData.last_name}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Password</label>
          {/* Show/hide toggle — same pattern as the sign-in form. Creating a password
              you cannot read is a guessing game; sign-in had this and sign-up did not. */}
          <div className="pw-field-wrap">
            <input
              type={showRegisterPw ? 'text' : 'password'}
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            <button
              type="button"
              className="pw-eye-btn"
              onClick={() => setShowRegisterPw(!showRegisterPw)}
              aria-label="Toggle password visibility"
            >{showRegisterPw ? '🙈' : '👁'}</button>
          </div>
          <small className="text-gray-500 text-12">
            Password must be at least 8 characters long
          </small>
        </div>

        {formData.referral_code && (
          <div className="rlp-codeField">
            <label className="form-label">Referral Code</label>
            <input
              type="text"
              value={formData.referral_code}
              readOnly
              className="form-input rlp-codeInput"
            />
          </div>
        )}

        {/* ── Consent gate ─────────────────────────────────────────────────────────────── */}
        <div className="consent-gate">
          <h4 className="consent-gate-title">Before you start</h4>
          <p className="consent-gate-intro">
            Two things to agree to. The second one is specific to the beta.
          </p>

          <button type="button" className="link-btn consent-toggle"
            onClick={() => setShowTerms(v => !v)}>
            {showTerms ? 'Hide' : 'Read'} the Beta Terms of Participation
          </button>

          {showTerms && (
            <div className="consent-terms">
              <p><strong>You get</strong> complete access to Healist.AI, free, for
                <strong> {trialDays} days</strong>. No credit card, no charge, and no automatic
                conversion to a paid plan when it ends.</p>
              <p><strong>We're asking two things in return:</strong> that you actually use Healist,
                and that you tell us what's wrong with it, what additional features you would find
                useful, or any other feedback you'd like to make sure we see.</p>

              <h5>If the account goes unused</h5>
              <ul>
                <li>After <strong>{inact.suspend_days ?? 21} days</strong> with no activity we
                  suspend access and offer your place to someone on the waiting list. We'll email
                  you on day {inact.remind_days ?? 15} and again on day {inact.final_days ?? 20};
                  the second is the last one, and it carries a one-click link to keep your account
                  active.</li>
                <li><strong>Your data is not deleted then.</strong> You can reactivate any time up
                  to day {inact.delete_days ?? 30} and everything will be exactly as you left it.</li>
                <li>After <strong>{inact.delete_days ?? 30} days</strong> with no activity, the
                  account and its health data are <strong>permanently deleted</strong>.</li>
              </ul>

              <h5>What "permanently deleted" means, exactly</h5>
              <p><strong>Deleted and unrecoverable:</strong> your profile, every health record you
                entered or uploaded, meals, medications, devices and their synced data, plans,
                reports, messages, and feedback you filed.</p>
              <p><strong>Retained, because the law requires it:</strong> records of <em>access</em>
                — a log of when health data was viewed, changed, exported or deleted, and a log of
                AI inferences run. HIPAA §164.312(b) requires these audit records be kept; Healist
                retains them for <strong>7 years</strong> (the rule requires 6).</p>
              <p><strong>Your identity is severed from them.</strong> When your account is deleted,
                the user reference on those audit rows is set to NULL. The record that "a health
                record was exported on this date" survives. The link saying it was <em>you</em>
                does not. We keep the audit trail; we do not keep you in it. You also receive a
                signed deletion certificate.</p>

              <h5>You can take your data with you</h5>
              <p>At any time you can export everything you've put into Healist as a standard
                <strong> FHIR</strong> health-data bundle — the same format your doctor's systems
                use — from your account settings.</p>

              <h5>This is a beta</h5>
              <p>Things will break, results will sometimes be wrong, and features will change.
                <strong> Healist is not a medical device and nothing in it is medical advice</strong>
                — talk to your doctor before acting on anything you see here.</p>
              <p className="consent-terms-foot">These beta terms are <em>in addition to</em> our
                standard Terms of Service and Privacy Policy, which continue to apply in full.</p>
            </div>
          )}

          <label className="consent-check">
            <input type="checkbox" checked={acceptedTos}
              onChange={e => setAcceptedTos(e.target.checked)} />
            <span>I have read and agree to the Healist.AI{' '}
              <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </label>

          <label className="consent-check">
            <input type="checkbox" checked={acceptedBeta}
              onChange={e => setAcceptedBeta(e.target.checked)} />
            <span>I understand I'm receiving <strong>free access in exchange for using Healist and
              giving feedback</strong>, and that if my account goes unused it will be suspended
              after <strong>{inact.suspend_days ?? 21} days</strong> and{' '}
              <strong>permanently deleted after {inact.delete_days ?? 30}</strong>, as described
              above.</span>
          </label>

          <div className="consent-sign">
            <label htmlFor="beta-sign">Type your full name to sign</label>
            <input id="beta-sign" type="text" className="form-input" value={signedName}
              onChange={e => setSignedName(e.target.value)}
              placeholder="Your full name" autoComplete="name" />
          </div>
        </div>

        <button type="submit" className="btn btn-primary w-full"
          disabled={loading || !consentReady}>
          {loading ? <div className="spinner"></div> : 'Sign Up'}
        </button>
        {!consentReady && (
          <p className="consent-hint">
            Please accept both agreements and type your name to continue.
          </p>
        )}

        <p className="text-center mt-4">
          Already have an account?{' '}
          <button type="button" className="link-btn" onClick={onToggleMode}>
            Sign in
          </button>
        </p>
      </form>

      {/* Account Verification Modal */}
      {showVerificationModal && !verifyLaterMessage && !cancelMessage && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-sm">
            <div className="modal-header">
              <h3 className="m-0">Account Verification</h3>
            </div>
            <div className="modal-body">

            {error && (
              <div className="alert-error-box">
                {error}
              </div>
            )}

            <div className="mb-16">
              <label className="label-block">
                SMS verification code
              </label>
              <input
                type="text"
                placeholder={`sent to ${formData.phone}`}
                value={verificationCodes.sms_code}
                onChange={(e) => setVerificationCodes(prev => ({
                  ...prev,
                  sms_code: e.target.value
                }))}
                maxLength={6}
                className="input-full"
              />
            </div>

            <div className="mb-5">
              <label className="label-block">
                Email verification code
              </label>
              <input
                type="text"
                placeholder={`sent to ${formData.email}`}
                value={verificationCodes.email_code}
                onChange={(e) => setVerificationCodes(prev => ({
                  ...prev,
                  email_code: e.target.value
                }))}
                maxLength={6}
                className="input-full"
              />
            </div>

            <div className="flex gap-12 justify-center flex-wrap">
              <button
                onClick={handleVerify}
                disabled={loading || !verificationCodes.email_code || !verificationCodes.sms_code}
                className="btn-primary-sm"
              >
                Verify
              </button>

              <button
                onClick={handleVerifyLater}
                disabled={loading}
                className="btn-secondary-sm"
              >
                Verify Later
              </button>

              <button
                onClick={handleResendCodes}
                disabled={loading}
                className="btn-secondary-sm"
              >
                Resend Codes
              </button>

              <button
                onClick={handleCancel}
                disabled={loading}
                className="btn-secondary-sm"
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Later Message Modal */}
      {verifyLaterMessage && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-xs">
            <div className="modal-body">
            <p className="mb-5 text-center">
              The verification codes sent will be active for 2 hours only. If you do not verify within that time period you will have to request new codes on the Account Verification page.
            </p>
            <div className="button-row">
              <button
                onClick={handleVerifyLaterOK}
                className="btn-primary-sm"
              >
                OK
              </button>
              <button
                onClick={handleVerifyLaterCancel}
                className="btn-secondary-sm"
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Choice Modal - NEW for Fix A */}
      {showCancelChoice && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-sm">
            <div className="modal-body">
            <p className="mb-20 text-center text-16">
              Do you want to cancel Account Creation or Just the Verification step?
            </p>
            <div className="button-row">
              <button
                onClick={handleCancelAccountCreation}
                className="btn-danger-md"
              >
                Cancel Account Creation
              </button>
              <button
                onClick={handleCancelVerificationOnly}
                className="btn-primary-md"
              >
                Cancel Verification
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Cancelled Confirmation Modal */}
      {accountCancelledMessage && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-xs">
            <div className="modal-body">
            <p className="mb-5 text-center">
              Account creation cancelled
            </p>
            <div className="flex-row-center">
              <button
                onClick={handleAccountCancelledOK}
                className="btn-primary-sm"
              >
                OK
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelMessage && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-xs">
            <div className="modal-body">
            <p className="mb-5 text-center">
              Account verification step has been cancelled. However your Account has been created. You can Sign In from the main page at any time to verify your account.
            </p>
            <div className="flex-row-center">
              <button
                onClick={handleCancelOK}
                className="btn-primary-sm"
              >
                OK
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegisterForm;
